import { DebugLogger } from '@affine/debug';
import { setupGlobal } from '@affine/env/global';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { atomEffect } from 'jotai-effect';

import type { TelemetryConsent } from '../telemetry-consent';
import {
  DEFAULT_TELEMETRY_CONSENT,
  normalizeTelemetryConsent,
} from '../telemetry-consent';

setupGlobal();

const logger = new DebugLogger('streamlain:settings');

export type AppSetting = {
  clientBorder: boolean;
  windowFrameStyle: 'frameless' | 'NativeTitleBar';
  enableBlurBackground: boolean;
  enableNoisyBackground: boolean;
  autoCheckUpdate: boolean;
  autoDownloadUpdate: boolean;
  /** @deprecated Read only for fail-closed legacy migration. */
  enableTelemetry?: boolean;
  telemetryConsent: TelemetryConsent;
  showLinkedDocInSidebar: boolean;
};
export const windowFrameStyleOptions: AppSetting['windowFrameStyle'][] = [
  'frameless',
  'NativeTitleBar',
];

export const APP_SETTINGS_STORAGE_KEY = 'streamlain-settings';
const LEGACY_APP_SETTINGS_STORAGE_KEY = 'affine-settings';

const appSettingsStorage = {
  getItem(key: string, initialValue: AppSetting): AppSetting {
    if (typeof localStorage === 'undefined') return initialValue;
    const current = localStorage.getItem(key);
    const legacy = localStorage.getItem(LEGACY_APP_SETTINGS_STORAGE_KEY);
    const stored = current ?? legacy;
    if (!stored) return initialValue;
    try {
      const parsed = JSON.parse(stored) as Partial<AppSetting>;
      return {
        ...initialValue,
        ...parsed,
        telemetryConsent: normalizeTelemetryConsent(parsed),
      };
    } catch {
      return initialValue;
    }
  },
  setItem(key: string, value: AppSetting): void {
    localStorage.setItem(key, JSON.stringify(value));
    localStorage.removeItem(LEGACY_APP_SETTINGS_STORAGE_KEY);
  },
  removeItem(key: string): void {
    localStorage.removeItem(key);
    localStorage.removeItem(LEGACY_APP_SETTINGS_STORAGE_KEY);
  },
};
const appSettingBaseAtom = atomWithStorage<AppSetting>(
  APP_SETTINGS_STORAGE_KEY,
  {
    clientBorder: BUILD_CONFIG.isElectron && !environment.isWindows,
    windowFrameStyle: 'frameless',
    enableBlurBackground: false,
    enableNoisyBackground: true,
    autoCheckUpdate: true,
    autoDownloadUpdate: true,
    telemetryConsent: DEFAULT_TELEMETRY_CONSENT,
    showLinkedDocInSidebar: false,
  },
  appSettingsStorage,
  {
    getOnInit: true,
  }
);

type SetStateAction<Value> = Value | ((prev: Value) => Value);

// todo(@pengx17): use global state instead
const appSettingEffect = atomEffect(get => {
  const settings = get(appSettingBaseAtom);
  // some values in settings should be synced into electron side
  if (BUILD_CONFIG.isElectron) {
    logger.debug('sync settings to electron', settings);
    // this api type in @affine/electron-api, but it is circular dependency this package, use any here
    (window as any).__apis?.updater
      .setConfig({
        autoCheckUpdate: settings.autoCheckUpdate,
        autoDownloadUpdate: settings.autoDownloadUpdate,
      })
      .catch((err: any) => {
        console.error(err);
      });
  }
});

export const appSettingAtom = atom<
  AppSetting,
  [SetStateAction<Partial<AppSetting>>],
  void
>(
  get => {
    get(appSettingEffect);
    return get(appSettingBaseAtom);
  },
  (_get, set, apply) => {
    set(appSettingBaseAtom, prev => {
      const next = typeof apply === 'function' ? apply(prev) : apply;
      return { ...prev, ...next };
    });
  }
);
